import type { AnalyticsSummaryResponse } from '@/utils/analyticsSummaryAPI';
import type { TileMetric } from './types';
import { TILE_METRIC_METADATA } from './types';

export interface TileValue {
  count: number;
  percentage?: number;
  formattedCount: string;
  formattedPercentage?: string;
  showPercentage: boolean;
}

/**
 * Determine if a metric is a "total" metric (no percentage needed)
 */
function isTotalMetric(metric: TileMetric): boolean {
  return metric === 'total_companies' || metric === 'total_pages';
}

/**
 * Determine if a metric is a company-level metric
 */
function isCompanyMetric(metric: TileMetric): boolean {
  return metric.startsWith('companies_') || metric === 'total_companies';
}

// Legacy metric migration mapping
const LEGACY_METRIC_MAP: Record<string, TileMetric> = {
  'percent_with_public_pricing': 'companies_with_public_pricing',
  'percent_with_freemium': 'companies_with_freemium',
  'percent_with_trial': 'companies_with_trial',
  'percent_with_contact_pricing': 'companies_with_contact_pricing',
  'percent_pages_with_addons': 'pages_with_addons'
};

/**
 * Calculate a specific tile metric from the summary API response
 */
export function calculateTileSummaryMetric(
  metric: TileMetric,
  data: AnalyticsSummaryResponse
): TileValue {
  // Handle legacy metrics - migrate to new ones
  const actualMetric = (LEGACY_METRIC_MAP as any)[metric] || metric;

  if ((LEGACY_METRIC_MAP as any)[metric]) {
    console.warn(`[tileSummaryMetrics] Legacy metric detected: ${metric}, using ${actualMetric} instead`);
  }

  const { summary, pricing_models } = data;

  let count: number;
  let percentage: number | undefined;
  let showPercentage = false;

  // Get the count value
  switch (actualMetric) {
    case 'total_companies':
      count = summary.total_companies;
      break;

    case 'total_pages':
      count = summary.total_pages;
      break;

    case 'companies_with_addons':
      count = summary.companies_with_addons;
      break;

    case 'companies_with_multiple_pages':
      count = summary.companies_with_multiple_pages;
      break;

    case 'companies_with_public_pricing':
      count = pricing_models.companies_with_public_pricing;
      break;

    case 'companies_with_freemium':
      count = pricing_models.companies_with_freemium;
      break;

    case 'companies_with_trial':
      count = pricing_models.companies_with_trial;
      break;

    case 'companies_with_contact_pricing':
      count = pricing_models.companies_with_contact_pricing;
      break;

    case 'pages_with_addons':
      // Use companies_with_addons as proxy since pages_with_addons doesn't exist in API
      count = summary.companies_with_addons ?? 0;
      break;

    case 'pages_with_license_pricing':
      count = summary.pages_with_license_pricing;
      break;

    case 'pages_with_usage_pricing':
      count = summary.pages_with_usage_pricing;
      break;

    case 'pages_with_credit_pricing':
      count = summary.pages_with_credit_pricing;
      break;

    case 'pages_with_thresholds':
      count = summary.pages_with_thresholds;
      break;

    case 'avg_plans_per_page':
      // This is already an average, not a count
      const totalPlans = (summary as any).total_plans ?? 0;
      if (summary.total_pages > 0 && totalPlans > 0) {
        count = totalPlans / summary.total_pages;
      } else {
        count = 0;
      }
      break;

    default:
      console.warn(`[tileSummaryMetrics] Unknown metric in calculation: ${actualMetric}`);
      count = 0;
  }

  // Calculate percentage for non-total metrics
  if (!isTotalMetric(actualMetric) && actualMetric !== 'avg_plans_per_page') {
    showPercentage = true;

    if (isCompanyMetric(actualMetric)) {
      // Company metrics: calculate percentage of total companies
      if (summary.total_companies > 0) {
        percentage = (count / summary.total_companies) * 100;
      } else {
        percentage = 0;
      }
    } else {
      // Page metrics: calculate percentage of total pages
      if (summary.total_pages > 0) {
        percentage = (count / summary.total_pages) * 100;
      } else {
        percentage = 0;
      }
    }
  }

  return {
    count,
    percentage,
    formattedCount: formatCount(count, actualMetric),
    formattedPercentage: percentage !== undefined ? `${percentage.toFixed(1)}%` : undefined,
    showPercentage
  };
}

/**
 * Format a count value based on its metric type
 */
function formatCount(value: number, metric: TileMetric): string {
  const metadata = TILE_METRIC_METADATA[metric];

  // Gracefully handle old/invalid metrics
  if (!metadata) {
    console.warn(`[tileSummaryMetrics] Unknown metric: ${metric}`);
    return value.toLocaleString();
  }

  switch (metadata.format) {
    case 'number':
      return value.toLocaleString();

    case 'decimal':
      return value.toFixed(1);

    default:
      return value.toString();
  }
}

/**
 * Get the display label for a tile (uses full displayLabel for clarity)
 */
export function getTileLabel(metric: TileMetric, customLabel?: string): string {
  if (customLabel && customLabel.trim() !== '') {
    return customLabel;
  }

  // Handle legacy metrics
  const actualMetric = (LEGACY_METRIC_MAP as any)[metric] || metric;

  const metadata = TILE_METRIC_METADATA[actualMetric];
  if (!metadata) {
    console.warn(`[tileSummaryMetrics] Unknown metric for label: ${actualMetric}`);
    return actualMetric; // Fallback to metric name
  }

  return metadata.displayLabel;
}

/**
 * Get the description for a tile metric
 */
export function getTileDescription(metric: TileMetric): string {
  return TILE_METRIC_METADATA[metric].description;
}
